/* Aurelia interactive sections runtime.
   Renders <template data-aur-region> blocks with a small component class:
   {{path}} interpolation, <sc-for list as>, <sc-if value>, on* event bindings. */
(function () {
  'use strict';

  class DCLogic {
    constructor() { this.state = this.state || {}; }
    setState(patch) {
      Object.assign(this.state, patch);
      if (this.__render) this.__render();
    }
  }
  window.DCLogic = DCLogic;

  const EXPR = /\{\{\s*([^}]+?)\s*\}\}/g;

  function resolve(expr, scope) {
    expr = expr.trim();
    if (expr === 'true') return true;
    if (expr === 'false') return false;
    let v = scope;
    for (const part of expr.split('.')) {
      if (v == null) return undefined;
      v = v[part];
    }
    return v;
  }

  function interpolate(str, scope) {
    return str.replace(EXPR, (_, e) => {
      const v = resolve(e, scope);
      return v == null ? '' : String(v);
    });
  }

  function single(str) {
    const m = /^\s*\{\{\s*([^}]+?)\s*\}\}\s*$/.exec(str || '');
    return m ? m[1] : null;
  }

  function process(node, scope) {
    if (node.nodeType === 3) {
      if (node.nodeValue.indexOf('{{') !== -1) node.nodeValue = interpolate(node.nodeValue, scope);
      return;
    }
    if (node.nodeType !== 1) return;
    const tag = node.tagName.toLowerCase();

    if (tag === 'sc-for') {
      const list = resolve(single(node.getAttribute('list')) || '', scope) || [];
      const as = node.getAttribute('as') || 'item';
      const frag = document.createDocumentFragment();
      list.forEach((item) => {
        const local = Object.assign({}, scope);
        local[as] = item;
        Array.from(node.childNodes).forEach((child) => {
          const c = child.cloneNode(true);
          frag.appendChild(c);
          process(c, local);
        });
      });
      node.replaceWith(frag);
      return;
    }

    if (tag === 'sc-if') {
      const v = resolve(single(node.getAttribute('value')) || '', scope);
      if (v) {
        const kids = Array.from(node.childNodes);
        const frag = document.createDocumentFragment();
        kids.forEach((k) => frag.appendChild(k));
        kids.forEach((k) => process(k, scope));
        node.replaceWith(frag);
      } else {
        node.remove();
      }
      return;
    }

    Array.from(node.attributes).forEach((attr) => {
      const name = attr.name;
      if (name.indexOf('hint-') === 0) { node.removeAttribute(name); return; }
      if (name.indexOf('on') === 0 && single(attr.value)) {
        const fn = resolve(single(attr.value), scope);
        node.removeAttribute(name);
        if (typeof fn === 'function') {
          node.addEventListener(name.slice(2).toLowerCase(), (ev) => {
            if (node.tagName === 'A' && node.getAttribute('href') === '#') ev.preventDefault();
            fn(ev);
          });
        }
        return;
      }
      if (attr.value.indexOf('{{') !== -1) node.setAttribute(name, interpolate(attr.value, scope));
    });
    Array.from(node.childNodes).forEach((child) => process(child, scope));
  }

  function snapshotFields(root) {
    const out = {};
    root.querySelectorAll('input[id], textarea[id], select[id]').forEach((el) => {
      out[el.id] = el.type === 'checkbox' || el.type === 'radio' ? { checked: el.checked } : { value: el.value };
    });
    return out;
  }

  function restoreFields(root, snap) {
    Object.keys(snap).forEach((id) => {
      const el = root.querySelector('#' + CSS.escape(id));
      if (!el) return;
      if ('checked' in snap[id]) el.checked = snap[id].checked;
      else el.value = snap[id].value;
    });
  }

  /* Mount a component on every region inside `section`. */
  window.aurMount = function (section, Comp, key) {
    if (!section) return;
    const comp = new Comp();
    comp.__key = key;
    const saved = sessionStorage.getItem('aur-state-' + key);
    if (saved && window.__aurPosted && window.__aurPosted[key]) {
      const init = Object.assign({}, comp.state);
      try { Object.assign(comp.state, JSON.parse(saved)); } catch (e) { /* ignore */ }
      Object.keys(init).forEach((k) => { if (init[k] === true) comp.state[k] = true; });
    }
    if (window.__aurPosted && window.__aurPosted[key] && comp.onPosted) comp.onPosted();
    const regions = Array.from(section.querySelectorAll('template[data-aur-region]')).map((tpl) => {
      const host = document.createElement('div');
      host.className = 'aur-region';
      host.style.display = 'contents';
      tpl.after(host);
      return { tpl, host };
    });
    comp.__render = function () {
      const vals = comp.renderVals();
      const snap = snapshotFields(section);
      const ae = document.activeElement;
      const focusId = ae && ae.id && section.contains(ae) && /^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName) ? ae.id : null;
      let selS = null, selE = null;
      if (focusId) { try { selS = ae.selectionStart; selE = ae.selectionEnd; } catch (e) { /* not a text field */ } }
      regions.forEach(({ tpl, host }) => {
        const frag = tpl.content.cloneNode(true);
        const wrap = document.createElement('div');
        wrap.appendChild(frag);
        Array.from(wrap.childNodes).forEach((c) => process(c, vals));
        host.replaceChildren(...Array.from(wrap.childNodes));
      });
      restoreFields(section, snap);
      if (focusId) {
        const el = section.querySelector('#' + CSS.escape(focusId));
        if (el && el !== document.activeElement) {
          el.focus({ preventScroll: true });
          if (selS != null) { try { el.setSelectionRange(selS, selE); } catch (e) { /* ignore */ } }
        }
      }
    };
    comp.__render();
    window['aur_' + key] = comp;
    return comp;
  };

  /* Collect labelled fields inside an element into Shopify contact[...] fields. */
  window.aurCollect = function (scopeEl) {
    const fields = {};
    let email = null;
    scopeEl.querySelectorAll('input, textarea, select').forEach((el) => {
      if (!el.id || el.type === 'hidden') return;
      const label = scopeEl.querySelector('label[for="' + el.id + '"]');
      const name = (label ? label.textContent : el.id).trim().replace(/[\[\]]/g, '');
      if (el.type === 'email' && !email) email = el;
      if (el.value) fields['contact[' + name + ']'] = el.value;
    });
    return { fields, email };
  };

  /* Submit a hidden Shopify form (rendered by Liquid) with extra fields. */
  window.aurSubmit = function (formId, fields, comp, emailEl) {
    const form = document.getElementById(formId);
    if (!form) return false;
    if (emailEl) {
      emailEl.required = true;
      if (!emailEl.value || !emailEl.checkValidity()) { emailEl.reportValidity(); emailEl.focus(); return false; }
      fields['contact[email]'] = emailEl.value;
    }
    form.querySelectorAll('input[data-aur-extra]').forEach((el) => el.remove());
    Object.keys(fields).forEach((name) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = fields[name];
      input.setAttribute('data-aur-extra', '');
      form.appendChild(input);
    });
    if (comp && comp.__key) {
      try { sessionStorage.setItem('aur-state-' + comp.__key, JSON.stringify(comp.state)); } catch (e) { /* ignore */ }
    }
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
    return true;
  };
})();
